import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import { Modal } from '../../../components/modal/Modal';
import { Button } from '../../../components/buttons/Button';
import {
    EXTERNAL_MEETING_LINK_ERROR,
    isExternalMeetingUrl,
    normalizeExternalMeetingUrl,
} from '../../../utils/externalMeetingUrl';
import './class-slots-panel.css';

const TITLE_MAX = 120;
const DESCRIPTION_MAX = 2000;

const MODES = {
    manual: {
        title: 'Nueva clase',
        subtitle: 'El horario empieza ahora. Al terminar la clase se registra la duración.',
        chips: [
            { label: 'Empieza ahora', soft: false },
            { label: 'Visible para alumnos', soft: true },
        ],
        confirmLabel: 'Crear clase',
        loadingLabel: 'Creando…',
        hint: 'Obligatorio · visible para alumnos en Clases',
        error: 'Escribe un título para poder crear la clase.',
    },
    adhoc: {
        title: 'Nueva sesión Zoom',
        subtitle: 'Si la grabas, queda en Anteriores con este título.',
        chips: [
            { label: 'Hasta 30 min', soft: false },
            { label: 'Inicio automático', soft: true },
        ],
        confirmLabel: 'Crear sesión',
        loadingLabel: 'Creando…',
        hint: 'Obligatorio · visible para alumnos en Clases',
        error: 'Escribe un título para poder crear la sesión.',
    },
    scheduled: {
        title: 'Iniciar clase',
        subtitle: 'Este título se usará en la grabación y en Anteriores.',
        chips: [
            { label: 'Clase programada', soft: false },
            { label: 'Disponible desde 10 min antes', soft: true },
        ],
        confirmLabel: 'Iniciar clase',
        loadingLabel: 'Iniciando…',
        hint: 'Obligatorio · visible para alumnos en Clases',
        error: 'Escribe un título para poder iniciar la clase.',
    },
};

export const ModalCreateZoomSession = ({
    open,
    setOpen,
    loading = false,
    onConfirm,
    mode = 'manual',
    slotLabel = '',
    allowExternalLink = false,
}) => {
    const cancelButtonRef = useRef(null);
    const inputRef = useRef(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [externalLink, setExternalLink] = useState('');
    const [touched, setTouched] = useState(false);

    const copy = MODES[mode] || MODES.manual;
    const isManual = mode === 'manual';
    const showLinkField = isManual && allowExternalLink;
    const trimmed = title.trim();
    const linkTrimmed = externalLink.trim();
    const linkValid = isExternalMeetingUrl(linkTrimmed);
    const canSubmit = trimmed.length > 0 && (!showLinkField || linkValid);
    const showTitleError = touched && !trimmed;
    const showLinkError = showLinkField && touched && Boolean(linkTrimmed) && !linkValid;
    const showError = showTitleError || showLinkError;

    useEffect(() => {
        if (!open) return undefined;
        setTitle('');
        setDescription('');
        setExternalLink('');
        setTouched(false);
        const id = requestAnimationFrame(() => inputRef.current?.focus());
        return () => cancelAnimationFrame(id);
    }, [open, mode]);

    const handleClose = () => {
        if (loading) return;
        setOpen(false);
    };

    const handleConfirm = () => {
        setTouched(true);
        if (!canSubmit) {
            inputRef.current?.focus();
            return;
        }
        if (isManual) {
            onConfirm?.({
                title: trimmed,
                description: description.trim(),
                ...(showLinkField ? { externalLink: normalizeExternalMeetingUrl(linkTrimmed) } : {}),
            });
            return;
        }
        onConfirm?.(trimmed);
    };

    return (
        <Modal open={open} setOpen={setOpen} cancelButtonRef={cancelButtonRef} autoCloseModal={!loading}>
            <div className={`zoom-session-modal${isManual ? ' zoom-session-modal--manual' : ''}`}>
                <header className="zoom-session-modal__header">
                    <div className="zoom-session-modal__icon" aria-hidden="true">
                        <VideoCameraIcon className="w-5 h-5" />
                    </div>
                    <div className="zoom-session-modal__heading">
                        <Dialog.Title as="h3" className="zoom-session-modal__title">
                            {copy.title}
                        </Dialog.Title>
                        <p className="zoom-session-modal__subtitle">
                            {slotLabel || copy.subtitle}
                        </p>
                        {slotLabel ? (
                            <p className="zoom-session-modal__subtitle zoom-session-modal__subtitle--secondary">
                                {copy.subtitle}
                            </p>
                        ) : null}
                    </div>
                </header>

                <div className="zoom-session-modal__meta" aria-hidden="true">
                    {copy.chips.map((chip) => (
                        <span
                            key={chip.label}
                            className={`zoom-session-modal__chip${chip.soft ? ' zoom-session-modal__chip--soft' : ''}`}
                        >
                            {chip.label}
                        </span>
                    ))}
                </div>

                <div className="zoom-session-modal__field">
                    <div className="zoom-session-modal__label-row">
                        <label className="zoom-session-modal__label" htmlFor="modal-zoom-session-title">
                            Título de la clase
                        </label>
                        <span className="zoom-session-modal__count" aria-live="polite">
                            {title.length}/{TITLE_MAX}
                        </span>
                    </div>
                    <input
                        ref={inputRef}
                        id="modal-zoom-session-title"
                        type="text"
                        className={`zoom-session-modal__input${showTitleError ? ' is-invalid' : ''}`}
                        placeholder="Ej. Repaso de vocabulario"
                        value={title}
                        maxLength={TITLE_MAX}
                        disabled={loading}
                        aria-invalid={showTitleError}
                        aria-describedby={showError ? 'modal-zoom-session-error' : 'modal-zoom-session-hint'}
                        onChange={(e) => setTitle(e.target.value)}
                        onBlur={() => setTouched(true)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isManual) {
                                e.preventDefault();
                                handleConfirm();
                            }
                        }}
                    />
                </div>

                {isManual ? (
                    <>
                        <div className="zoom-session-modal__field">
                            <div className="zoom-session-modal__label-row">
                                <label className="zoom-session-modal__label" htmlFor="modal-zoom-session-desc">
                                    Descripción
                                </label>
                                <span className="zoom-session-modal__count" aria-live="polite">
                                    {description.length}/{DESCRIPTION_MAX}
                                </span>
                            </div>
                            <textarea
                                id="modal-zoom-session-desc"
                                className="zoom-session-modal__textarea"
                                placeholder="Opcional · temas, materiales, avisos…"
                                value={description}
                                maxLength={DESCRIPTION_MAX}
                                disabled={loading}
                                rows={3}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                        {showLinkField ? (
                            <div className="zoom-session-modal__field">
                                <div className="zoom-session-modal__label-row">
                                    <label className="zoom-session-modal__label" htmlFor="modal-zoom-session-link">
                                        Link de la llamada
                                    </label>
                                    <span className="zoom-session-modal__count">Opcional</span>
                                </div>
                                <input
                                    id="modal-zoom-session-link"
                                    type="url"
                                    inputMode="url"
                                    autoComplete="off"
                                    spellCheck={false}
                                    className={`zoom-session-modal__input${showLinkError ? ' is-invalid' : ''}`}
                                    placeholder="Link de Meet, Zoom, Teams o Webex"
                                    value={externalLink}
                                    disabled={loading}
                                    aria-invalid={showLinkError}
                                    onChange={(e) => setExternalLink(e.target.value)}
                                    onBlur={() => setTouched(true)}
                                />
                            </div>
                        ) : null}
                    </>
                ) : null}

                {showError ? (
                    <p id="modal-zoom-session-error" className="zoom-session-modal__error" role="alert">
                        {showLinkError ? EXTERNAL_MEETING_LINK_ERROR : copy.error}
                    </p>
                ) : (
                    <p id="modal-zoom-session-hint" className="zoom-session-modal__hint">
                        {showLinkField
                            ? 'El título es obligatorio. El link de Meet, Zoom, Teams o Webex es opcional y queda visible para los alumnos.'
                            : copy.hint}
                    </p>
                )}

                <footer className="zoom-session-modal__footer">
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={handleClose}
                        ref={cancelButtonRef}
                        disabled={loading}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        variant="gradient"
                        size="sm"
                        className="zoom-session-modal__btn-create"
                        loading={loading}
                        loadingLabel={copy.loadingLabel}
                        disabled={!canSubmit || loading}
                        onClick={handleConfirm}
                    >
                        {copy.confirmLabel}
                    </Button>
                </footer>
            </div>
        </Modal>
    );
};
